// Bottom sheet with three resting heights (peek, half, full) on phones; a side panel on wide screens.
// Drag the grip, tap it to cycle, or use Enter/Space on the handle.

const STATES = ['peek', 'half', 'full'];
const WIDE = '(min-width: 900px)';

export function createSheet({ sheet, grip, handle, onChange }) {
  const wide = window.matchMedia(WIDE);
  let state = sheet.dataset.state || 'peek';

  const heightFor = (s) => {
    const vh = window.innerHeight;
    if (s === 'peek') return Math.min(196, Math.round(vh * 0.3));
    if (s === 'half') return Math.round(vh * 0.56);
    return vh - 64;
  };

  function visibleHeight() {
    return wide.matches ? 0 : heightFor(state);
  }

  function apply(animate = true) {
    sheet.dataset.state = state;
    sheet.classList.toggle('no-anim', !animate);
    const full = heightFor('full');
    sheet.style.height = `${full}px`;
    sheet.style.transform = wide.matches ? '' : `translateY(${full - heightFor(state)}px)`;
    document.documentElement.style.setProperty('--sheet-visible', `${visibleHeight()}px`);
    handle.setAttribute('aria-label', state === 'full' ? 'Collapse panel' : 'Expand panel');
    handle.setAttribute('aria-expanded', String(state !== 'peek'));
    onChange?.(state, visibleHeight());
  }

  function set(s, animate = true) {
    if (!STATES.includes(s)) return;
    state = s;
    apply(animate);
  }

  // Dragging
  let drag = null;
  grip.addEventListener('pointerdown', (e) => {
    if (wide.matches) return;
    drag = { y: e.clientY, start: heightFor(state), moved: false };
    grip.setPointerCapture(e.pointerId);
    sheet.classList.add('no-anim');
  });
  grip.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dy = e.clientY - drag.y;
    if (Math.abs(dy) > 4) drag.moved = true;
    const full = heightFor('full');
    const h = Math.min(full, Math.max(heightFor('peek') - 40, drag.start - dy));
    sheet.style.transform = `translateY(${full - h}px)`;
  });
  const end = (e) => {
    if (!drag) return;
    const dy = e.clientY - drag.y;
    const h = drag.start - dy;
    const moved = drag.moved;
    drag = null;
    if (!moved) { set(state === 'peek' ? 'half' : state === 'half' ? 'full' : 'peek'); return; }
    // Snap to the nearest resting height, biased by the direction of the flick.
    const nearest = STATES.map((s) => [s, Math.abs(heightFor(s) - h)]).sort((a, b) => a[1] - b[1])[0][0];
    set(nearest);
  };
  grip.addEventListener('pointerup', end);
  grip.addEventListener('pointercancel', end);

  handle.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); set(STATES[Math.min(2, STATES.indexOf(state) + 1)]); }
    if (e.key === 'ArrowDown') { e.preventDefault(); set(STATES[Math.max(0, STATES.indexOf(state) - 1)]); }
  });
  handle.addEventListener('click', (e) => {
    // Pointer taps are handled in `end`; this catches keyboard activation (Enter/Space).
    if (e.detail === 0) set(state === 'full' ? 'peek' : STATES[STATES.indexOf(state) + 1]);
  });

  window.addEventListener('resize', () => apply(false));
  wide.addEventListener('change', () => apply(false));
  apply(false);

  return {
    set,
    get state() { return state; },
    get wide() { return wide.matches; },
    visibleHeight,
  };
}
